import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  Stack,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useAgentReport } from '../../../../contexts/AgentReportContext';

const Location = ({ agentId }) => {
  const theme = useTheme();
  const { fetchSingleAgentLocationHistory, fetchSingleAgentLocationDetails } = useAgentReport();

  // Location history state
  const [locationHistory, setLocationHistory] = useState([]);
  const [selectedLocationDate, setSelectedLocationDate] = useState(null);
  const [loadingLocationHistory, setLoadingLocationHistory] = useState(false);
  const [loadingDateDetails, setLoadingDateDetails] = useState(false);
  const [selectedDateLocation, setSelectedDateLocation] = useState(null);
  const [selectedDateLocations, setSelectedDateLocations] = useState([]); // All locations for selected date
  const [selectedLocationPointId, setSelectedLocationPointId] = useState(null); // Selected location point ID from the list

  useEffect(() => {
    fetchLocationHistoryData();
  }, [agentId]);

  const handleLocationDateSelect = async (dateItem) => {
    setSelectedLocationDate(dateItem.date);
    setSelectedLocationPointId(null); // Reset selected point when date changes
    
    // Only set location if it exists and has valid coordinates
    if (dateItem.latest_location && dateItem.latest_location.latitude && dateItem.latest_location.longitude) {
      setSelectedDateLocation(dateItem.latest_location);
    } else {
      setSelectedDateLocation(null);
    }

    // Fetch detailed locations for this specific date (with addresses)
    if (agentId && dateItem.date) {
      setLoadingDateDetails(true);
      try {
        const response = await fetchSingleAgentLocationDetails(agentId, dateItem.date, true);
        if (response && response.locations) {
          setSelectedDateLocations(response.locations);
        } else {
          setSelectedDateLocations([]);
        }
      } catch (error) {
        console.error('Error fetching location details for date:', error);
        setSelectedDateLocations([]);
      } finally {
        setLoadingDateDetails(false);
      }
    }
  };

  const handleLocationPointSelect = (locationPoint) => {
    setSelectedLocationPointId(locationPoint.id);
    // Update the map to show this specific location point
    setSelectedDateLocation({
      latitude: locationPoint.latitude,
      longitude: locationPoint.longitude,
      accuracy: locationPoint.accuracy,
      source: locationPoint.source,
      timestamp: locationPoint.timestamp
    });
  };

  const fetchLocationHistoryData = async () => {
    if (!agentId) return;
    setLoadingLocationHistory(true);
    try {
      // Fetch only counts and latest location (no addresses, no detailed locations)
      // This is much faster as it doesn't fetch all location details
      const response = await fetchSingleAgentLocationHistory(agentId, false);
      if (response && response.location_history) {
        setLocationHistory(response.location_history);
        // Auto-select the first date (most recent) with a valid location if available
        if (response.location_history.length > 0 && !selectedLocationDate) {
          const firstDateWithLocation = response.location_history.find(
            item => item.latest_location && item.latest_location.latitude && item.latest_location.longitude
          );
          if (firstDateWithLocation) {
            // Use handleLocationDateSelect to properly set all state
            // This will trigger the detailed fetch for the selected date
            handleLocationDateSelect(firstDateWithLocation);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching location history:', error);
    } finally {
      setLoadingLocationHistory(false);
    }
  };

  // Generate Google Maps Embed URL for selected date location
  const getLocationMapEmbedUrl = () => {
    // Use selected location point if one is selected, otherwise use latest location
    let locationToShow = selectedDateLocation;
    
    if (selectedLocationPointId && selectedDateLocations.length > 0) {
      const selectedPoint = selectedDateLocations.find(loc => loc.id === selectedLocationPointId);
      if (selectedPoint) {
        locationToShow = {
          latitude: selectedPoint.latitude,
          longitude: selectedPoint.longitude,
          accuracy: selectedPoint.accuracy,
          source: selectedPoint.source,
          timestamp: selectedPoint.timestamp
        };
      }
    }
    
    if (!locationToShow) return null;
    
    const lat = locationToShow.latitude;
    const lng = locationToShow.longitude;
    
    return `https://www.google.com/maps?q=${lat},${lng}&output=embed&z=15`;
  };

  // Get the currently displayed location (selected point or latest)
  const getCurrentDisplayLocation = () => {
    if (selectedLocationPointId && selectedDateLocations.length > 0) {
      const selectedPoint = selectedDateLocations.find(loc => loc.id === selectedLocationPointId);
      if (selectedPoint) {
        return {
          latitude: selectedPoint.latitude,
          longitude: selectedPoint.longitude,
          accuracy: selectedPoint.accuracy,
          source: selectedPoint.source,
          timestamp: selectedPoint.timestamp
        };
      }
    }
    return selectedDateLocation;
  };

  return (
    <Box>
      {/* Location History Section */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Location Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Agent location tracking for the last 30 days
          </Typography>
        </Box>

        {loadingLocationHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container sx={{ minHeight: 'calc(100vh - 400px)' }}>
            {/* Left Panel - Date List */}
            <Grid item xs={12} md={4} sx={{ borderRight: { md: 1 }, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Select Date
                </Typography>
                {locationHistory.length > 0 ? (
                  <Stack spacing={1}>
                    {locationHistory.map((dateItem) => (
                      <Card
                        key={dateItem.date}
                        onClick={() => handleLocationDateSelect(dateItem)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: selectedLocationDate === dateItem.date ? 2 : 1,
                          borderColor: selectedLocationDate === dateItem.date 
                            ? 'primary.main' 
                            : 'divider',
                          bgcolor: selectedLocationDate === dateItem.date 
                            ? alpha(theme.palette.primary.main, 0.08) 
                            : 'background.paper',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {dayjs(dateItem.date).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(dateItem.date).format('dddd')}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip 
                              label={`${dateItem.count} loc${dateItem.count !== 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                        {dateItem.latest_location && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            <LocationOnIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                            {dateItem.latest_location.latitude.toFixed(4)}, {dateItem.latest_location.longitude.toFixed(4)}
                          </Typography>
                        )}
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <LocationOnIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No location data available for the last 30 days
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Right Panel - Google Maps */}
            <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {getCurrentDisplayLocation() && getLocationMapEmbedUrl() ? (
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Location for {selectedLocationDate ? dayjs(selectedLocationDate).format('MMM DD, YYYY') : 'Selected Date'}
                        {selectedLocationPointId && (
                          <Chip 
                            label="Point Selected" 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <LocationOnIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                        {getCurrentDisplayLocation().latitude.toFixed(6)}, {getCurrentDisplayLocation().longitude.toFixed(6)}
                      </Typography>
                      {getCurrentDisplayLocation().accuracy && (
                        <Typography variant="caption" color="text.secondary">
                          Accuracy: {getCurrentDisplayLocation().accuracy.toFixed(2)}m
                        </Typography>
                      )}
                      {getCurrentDisplayLocation().timestamp && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Time: {new Date(getCurrentDisplayLocation().timestamp).toLocaleTimeString()}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 450,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                    >
                      <iframe
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={getLocationMapEmbedUrl()}
                        title="Location Map"
                      />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MapIcon />}
                        onClick={() => {
                          const currentLoc = getCurrentDisplayLocation();
                          if (currentLoc) {
                            const url = `https://www.google.com/maps?q=${currentLoc.latitude},${currentLoc.longitude}`;
                            window.open(url, '_blank');
                          }
                        }}
                      >
                        Open in Google Maps
                      </Button>
                    </Box>
                    
                    {/* Location Tracking Points List */}
                    {loadingDateDetails ? (
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading location details...
                        </Typography>
                      </Box>
                    ) : selectedDateLocations.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                          Location Tracking Points ({selectedDateLocations.length})
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                          {selectedDateLocations.map((loc, index) => {
                            const isSelected = selectedLocationPointId === loc.id;
                            return (
                              <Box 
                                key={loc.id}
                                onClick={() => handleLocationPointSelect(loc)}
                                sx={{ 
                                  p: 1.5, 
                                  mb: 1, 
                                  bgcolor: isSelected 
                                    ? alpha(theme.palette.primary.main, 0.15)
                                    : alpha(theme.palette.primary.main, 0.05),
                                  borderRadius: 1,
                                  border: isSelected ? 2 : 1,
                                  borderColor: isSelected 
                                    ? 'primary.main'
                                    : alpha(theme.palette.primary.main, 0.1),
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'translateX(4px)',
                                  },
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                    Point {index + 1} - {new Date(loc.timestamp).toLocaleTimeString()}
                                  </Typography>
                                  {isSelected && (
                                    <Chip 
                                      label="Selected" 
                                      size="small" 
                                      color="primary" 
                                      sx={{ fontSize: '0.65rem', height: 18 }}
                                    />
                                  )}
                                </Box>
                                {loc.address && (
                                  <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5, lineHeight: 1.4 }}>
                                    <LocationOnIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'primary.main' }} />
                                    {loc.address}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                                  {loc.accuracy && ` • Accuracy: ${loc.accuracy.toFixed(2)}m`}
                                  {loc.source && ` • Source: ${loc.source}`}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 450,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`
                    }}
                  >
                    <MapIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedLocationDate ? 'No Location Data Available' : 'Select a Date'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {selectedLocationDate 
                        ? 'No location tracking data found for the selected date.'
                        : 'Please select a date from the list to view the location on the map.'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default Location;

